/* eslint-disable no-use-before-define */
import { all, takeLatest } from '@redux-saga/core/effects'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import Api from '@/api/_legacy'
import {
  ApiAccountApprovalEvent,
  ApiTradeApproval,
  ApiTradeRejection
} from '@/api/_legacy/generated-api'
import { UserRole, UserType } from '@/types/user'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { TradeActivationStep } from '../../../../tradebuilder-v2/types'
import { Errors } from '../../../../tradebuilder-v2/util'
import { parseApproveErrors } from '../../../util/validator'

const TENANT_ID = 'absa'
export type TradeApproveStepState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  canProceed: boolean
  errors: Errors
  generalErrors?: []
  hasCompleted: boolean
  rejectSuccess?: boolean
  success?: boolean
  tradeDocumentationUrl: string
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

const initialState: TradeApproveStepState = {
  canProceed: true,
  errors: {},
  generalErrors: [],
  approvalEvents: [],
  accountId: undefined,
  tradeDocumentationUrl: undefined,
  userName: undefined,
  userType: undefined,
  userRoles: [],
  hasCompleted: false
}

export type TradeApproveFormState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  status: TradeActivationStep
  tradeDocumentationUrl: string
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

export const tradeApproveSlice = createSlice({
  name: 'tradeApproveSlice',
  initialState,
  reducers: {
    reset: state => {
      state.canProceed = true
      state.errors = {}
      state.hasCompleted = false
      state.success = undefined
    },
    initialise: (state, action: PayloadAction<TradeApproveFormState>) => {
      state.approvalEvents = action.payload.approvalEvents
        ? action.payload.approvalEvents.filter(x => x.statusBefore === 'PendingApproval')
        : []
      state.hasCompleted = action.payload.status > TradeActivationStep.approve
      state.accountId = action.payload.accountId
      state.userName = action.payload.userName
      state.userType = action.payload.userType
      state.userRoles = action.payload.userRoles
      state.tradeDocumentationUrl = action.payload.tradeDocumentationUrl
      state.success = false
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    postTradeApproveSuccess: state => {
      state.errors = {}
      state.success = true
    },
    postTradeApproveFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    postTradeRejectSuccess: state => {
      state.rejectSuccess = true
      state.errors = {}
    },
    requestMoveNext: () => {
      // dummy comment to keep linter happy
    },
    moveNext: () => {
      // dummy comment to keep linter happy
    },
    moveBack: () => {
      // dummy comment to keep linter happy
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const postTradeApprove = (accountId: string, comments: string) => async dispatch => {
  try {
    const request: ApiTradeApproval = {
      comments
    }
    await Api.tenant.approveCreate(accountId, TENANT_ID, request, { format: 'json' })
    dispatch(actions.postTradeApproveSuccess())
  } catch (response) {
    const errors: Errors = parseApproveErrors(response)
    dispatch(actions.postTradeApproveFailure(errors))
  }
}

export const postTradeReject = (accountId: string, comments: string) => async dispatch => {
  try {
    const request: ApiTradeRejection = {
      comments
    }
    await Api.tenant.rejectCreate(accountId, TENANT_ID, request, { format: 'json' })
    dispatch(actions.postTradeRejectSuccess())
  } catch (response) {
    const errors: Errors = parseApproveErrors(response)
    dispatch(actions.postTradeApproveFailure(errors))
  }
}

function* requestMoveNextSaga() {
  //
}

export function* termsRootSaga() {
  yield all([takeLatest('tradeApproveSlice/requestMoveNext', requestMoveNextSaga)])
}

export const actions = { ...tradeApproveSlice.actions }

export type TradeApproveActions = typeof actions

export default tradeApproveSlice.reducer
